import { useCallback, useEffect, useMemo, useState } from 'react';
import { listDict, DictEntry } from '../../api/dict';
import {
    listMMDeviceRefs,
    listMMGroups,
    loadMModesCached,
    mmModeLabel,
    MMGroup,
    MMMode,
} from '../../api/mmode';
import { ParamSchema, schemaCovers } from '../../api/parameter';

// 参数关键字段 (设备 / 加工模式 / 加工类型) 的候选口径, 参数录入页与参数批量导入共用:
//   - 加工模式: 只列本框架建了模板的模式组里的模式值 (模板按设备建: 选了设备只算该设备的模板, 存量机型模板按机型);
//     设备引用 (mm_device_mode) 做二次收敛, 交集为空退回模板候选; 框架没有带模式段的模板时退回全量字典.
//   - 加工类型: 只列 (设备, 已选模式组) 命中模板里出现过的类型; 模式未选取本设备全部模板的并集;
//     无带类型段的模板时退回字典全量.
// archSchemas 由调用方给 (录入页只在有编辑权时拉, 导入弹窗自己拉), 这里不重复请求.
export function useParamKeyOptions(opts: {
    archCode: string;
    family: string;
    deviceKey: string;
    processingMode: string;
    archSchemas: ParamSchema[];
}) {
    const { archCode, family, deviceKey, processingMode, archSchemas } = opts;
    const [mmodes, setMModes] = useState<MMMode[]>([]);
    // 归属组字典: 加工模式下拉的显示名与模板管理同一套 (组中文名, 例 "激光圆柱"),
    // 避免同一概念两处两个叫法 (模式值中文 "旋转附件加工" vs 模板键 "激光圆柱").
    const [mmGroups, setMMGroups] = useState<MMGroup[]>([]);
    useEffect(() => {
        loadMModesCached().then(setMModes);
        listMMGroups()
            .then(setMMGroups)
            .catch(() => setMMGroups([]));
    }, []);

    // 加工类型字典 (process_type / scope=archCode)
    const [subProcessOpts, setSubProcessOpts] = useState<DictEntry[]>([]);
    useEffect(() => {
        if (!archCode) {
            setSubProcessOpts([]);
            return;
        }
        listDict('process_type', { scope: archCode })
            .then(setSubProcessOpts)
            .catch(() => setSubProcessOpts([]));
    }, [archCode]);

    // 当前设备引用的模式值 (mm_device_mode); null = 未维护引用
    const [deviceModeValues, setDeviceModeValues] = useState<string[] | null>(
        null,
    );
    useEffect(() => {
        if (!deviceKey) {
            setDeviceModeValues(null);
            return;
        }
        let alive = true;
        listMMDeviceRefs(deviceKey)
            .then((refs) => {
                if (alive)
                    setDeviceModeValues(
                        refs.filter((r) => r.enabled).map((r) => r.modeValue),
                    );
            })
            .catch(() => {
                if (alive) setDeviceModeValues(null);
            });
        return () => {
            alive = false;
        };
    }, [deviceKey]);

    const modeOptions = useMemo(() => {
        const tplGroups = new Set(
            archSchemas
                .filter(
                    (s) =>
                        s.groupCode && schemaCovers(s, deviceKey, family),
                )
                .map((s) => s.groupCode),
        );
        let list =
            tplGroups.size > 0
                ? mmodes.filter((m) => tplGroups.has(m.groupCode))
                : mmodes;
        if (deviceModeValues && deviceModeValues.length > 0) {
            const allow = new Set(deviceModeValues);
            const hit = list.filter((m) => allow.has(m.value));
            if (hit.length > 0) list = hit;
        }
        return list;
    }, [archSchemas, mmodes, deviceModeValues, deviceKey, family]);

    // 加工模式值 → 归属组编码 (模板键 groupCode 段, 与后端 mmGroupCodeFor 同一口径)
    const groupCodeOf = useCallback(
        (modeValue: string): string =>
            mmodes.find((m) => m.value === modeValue)?.groupCode || '',
        [mmodes],
    );

    const subProcessShownOpts = useMemo(() => {
        const selGroup = groupCodeOf(processingMode);
        const tplTypes = new Set(
            archSchemas
                .filter(
                    (s) =>
                        s.processType &&
                        schemaCovers(s, deviceKey, family) &&
                        (!s.groupCode || !selGroup || s.groupCode === selGroup),
                )
                .map((s) => s.processType),
        );
        if (tplTypes.size === 0) return subProcessOpts;
        return subProcessOpts.filter((o) => tplTypes.has(o.code));
    }, [archSchemas, subProcessOpts, groupCodeOf, processingMode, deviceKey, family]);

    // 加工模式显示名 = 模式值自己的中文名 (与导入模板 xlsx 下拉同一文案, 例 "平面加工");
    // 候选内中文名重名 / 缺失时带上原值区分. 归属组中文名 (例 "激光平面") 作分组标题, 见 modeGroups.
    const modeLabel = useMemo(() => {
        const count = new Map<string, number>();
        for (const m of modeOptions)
            count.set(m.nameZh, (count.get(m.nameZh) || 0) + 1);
        return (m: MMMode): string =>
            m.nameZh && (count.get(m.nameZh) || 0) <= 1
                ? m.nameZh
                : mmModeLabel(m);
    }, [modeOptions]);

    // 候选按归属组分组 (组序沿用候选顺序), 下拉用 optgroup 渲染: 组名与模板管理的模板键一致.
    const modeGroups = useMemo(() => {
        const out: { code: string; name: string; modes: MMMode[] }[] = [];
        for (const m of modeOptions) {
            let g = out.find((x) => x.code === m.groupCode);
            if (!g) {
                const name = mmGroups.find(
                    (x) => x.code === m.groupCode,
                )?.nameZh;
                g = {
                    code: m.groupCode,
                    name: name || m.groupCode || '未归组',
                    modes: [],
                };
                out.push(g);
            }
            g.modes.push(m);
        }
        return out;
    }, [modeOptions, mmGroups]);

    return {
        mmodes,
        modeOptions,
        modeGroups,
        modeLabel,
        groupCodeOf,
        subProcessOpts,
        subProcessShownOpts,
    };
}
